/**
 * The local sign-in flow as an explicit state machine, separate from the component that draws it.
 *
 * ⭐⭐ SEPARATED SO IT CAN BE TESTED WITHOUT A DOM. The rules worth protecting here are about ORDER —
 *   that a password produces a challenge and never a session, that enrolment happens before the code
 *   box appears, that a failure returns you to the step you can act on — and a test that has to
 *   render, type and click to assert an ordering is a test of the markup as much as of the rule.
 *
 * ⛔ THE CHALLENGE IS A PARTIAL CREDENTIAL AND LIVES ONLY IN THIS STATE. It is never written to
 *   localStorage, sessionStorage, a cookie or the URL. Combined with a phone it becomes a session, so
 *   persisting it would leave half a login on the disk of a shared machine, surviving a tab close.
 */
import type { EnrolmentOffer, LoginChallenge } from '../api/types';

/**
 * ⭐ FIVE steps, not a `step: number`. Each carries exactly the data that step needs and nothing
 *   else — so `code` cannot be entered before a challenge exists, and the enrolment QR cannot be
 *   rendered without an offer, because there is no state in which those fields are both present and
 *   meaningless.
 */
export type LocalLoginState =
  | { step: 'credentials'; error: string | null; busy: boolean }
  | { step: 'totp'; challenge: string; error: string | null; busy: boolean }
  | { step: 'enrol'; challenge: string; offer: EnrolmentOffer; error: string | null; busy: boolean }
  | { step: 'enrol-loading'; challenge: string; error: string | null }
  | { step: 'done' };

export const initialLocalLoginState: LocalLoginState = {
  step: 'credentials',
  error: null,
  busy: false,
};

export type LocalLoginEvent =
  | { type: 'submit-credentials' }
  | { type: 'challenge-issued'; challenge: LoginChallenge }
  | { type: 'offer-loaded'; offer: EnrolmentOffer }
  | { type: 'submit-code' }
  | { type: 'authenticated' }
  | { type: 'failed'; message: string }
  | { type: 'restart' };

/**
 * ⚠️ A FAILURE NEVER ADVANCES A STEP, and never silently drops back to `credentials` either.
 *
 * ⭐⭐ Dropping back would be the intuitive "start again" and is wrong in the case that matters: a
 *   mistyped six digits would throw away a challenge that is still perfectly valid and make the user
 *   retype their password. Worse, on the ENROLMENT branch it would discard a pending secret they have
 *   already scanned into their phone — leaving an authenticator entry that will never work and no
 *   indication of why. ⇒ A failure keeps the step and attaches a reason.
 */
export function localLoginReducer(
  state: LocalLoginState,
  event: LocalLoginEvent,
): LocalLoginState {
  switch (event.type) {
    case 'submit-credentials':
      return state.step === 'credentials' ? { ...state, busy: true, error: null } : state;

    case 'challenge-issued':
      // ⭐ The API's discriminator decides the branch. This client does not infer it from anything
      //   it knows about the account, because it knows nothing about the account at this point.
      return event.challenge.status === 'totp_required'
        ? { step: 'totp', challenge: event.challenge.challenge, error: null, busy: false }
        : { step: 'enrol-loading', challenge: event.challenge.challenge, error: null };

    case 'offer-loaded':
      return state.step === 'enrol-loading'
        ? { step: 'enrol', challenge: state.challenge, offer: event.offer, error: null, busy: false }
        : state;

    case 'submit-code':
      if (state.step === 'totp' || state.step === 'enrol') {
        return { ...state, busy: true, error: null };
      }
      return state;

    case 'authenticated':
      return { step: 'done' };

    case 'failed':
      // ⛔ Stay on the current step. See the note above — this is the whole reason the reducer exists.
      if (state.step === 'done') return state;
      if (state.step === 'enrol-loading') {
        // The one exception: an offer we could not fetch leaves nothing to act on, so the only
        // honest move is back to the start with the reason attached.
        return { step: 'credentials', error: event.message, busy: false };
      }
      return { ...state, busy: false, error: event.message };

    case 'restart':
      return initialLocalLoginState;

    default:
      return state;
  }
}

/**
 * ⭐ The submit button's enabled state, in one place, so the two code-entry steps cannot disagree.
 *   A six-digit field that accepts five and then 400s is a round trip spent telling the user
 *   something the form already knew.
 */
export function canSubmitCode(code: string): boolean {
  return /^\d{6}$/.test(code);
}

export const MIN_PASSWORD_LENGTH = 12;

/** Mirrors the API's own minimum, so the refusal happens before the network rather than after. */
export function canSubmitNewPassword(password: string, confirmation: string): boolean {
  return password.length >= MIN_PASSWORD_LENGTH && password === confirmation;
}
